import { routePaths } from 'appConfig';
import cn from 'classnames';
import { Link } from 'components/common';
import {
  ProductCategory,
  SpecialProductCategory,
} from 'lib/productCategory';
import { pathToTo } from 'lib/router';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getElementHeight, getStickyHeaderElement } from 'utils';
import { CollectionMenu } from './types';

const useFakeSticky = (offset?: number) => {
  const [isFixed, setIsFixed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const triggerHeight =
        offset ?? getElementHeight(getStickyHeaderElement());
      setIsFixed(window.scrollY >= triggerHeight);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [offset]);

  return isFixed;
};

// Used for saving scroll position when query changes
const SCROLL_KEY = 'productCategoriesScrollLeft';

const ProductCategoriesTabs = ({
  className,
  collections,
  categories,
  specialProductCategories,
  isLoading,
  isTieredUser,
}: {
  className?: string;
  collections: CollectionMenu[];
  categories: ProductCategory[];
  specialProductCategories: SpecialProductCategory[];
  isLoading?: boolean;
  isTieredUser?: boolean;
}) => {
  const { query } = useRouter();
  const tabListRef = useRef<HTMLDivElement>(null);

  const [isLeftButtonVisible, setIsLeftButtonVisible] = useState(
    false,
  );
  const [isRightButtonVisible, setIsRightButtonVisible] = useState(
    false,
  );

  const handleScroll = useCallback(() => {
    const el = tabListRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    setIsLeftButtonVisible(scrollLeft > 0);
    setIsRightButtonVisible(
      scrollLeft + clientWidth < scrollWidth - 5,
    );
    sessionStorage.setItem(SCROLL_KEY, String(scrollLeft));
  }, []);

  useEffect(() => {
    const el = tabListRef.current;
    if (!el) return;

    const savedScroll = Number(
      sessionStorage.getItem(SCROLL_KEY) || 0,
    );

    requestAnimationFrame(() => {
      el.scrollLeft = savedScroll;
      handleScroll();
    });

    const resizeObserver = new ResizeObserver(() => handleScroll());
    resizeObserver.observe(el);

    el.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      resizeObserver.disconnect();
      el.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const el = tabListRef.current;
      if (!el) return;

      const savedScroll = Number(
        sessionStorage.getItem(SCROLL_KEY) || 0,
      );
      el.scrollLeft = savedScroll;
      handleScroll();
    }, 100);

    return () => clearTimeout(timeout);
  }, [handleScroll]);

  const handleArrowClicked = useCallback((amount: number) => {
    if (tabListRef.current) {
      tabListRef.current.scrollTo({
        left: tabListRef.current.scrollLeft + amount,
        behavior: 'smooth',
      });

      sessionStorage.setItem(
        SCROLL_KEY,
        String(tabListRef.current.scrollLeft + amount),
      );
    }
  }, []);

  const isFixed = useFakeSticky();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [query]);

  return (
    <div
      className={cn(
        'tw-bg-white tw-w-full tw-z-10',
        isFixed ? 'tw-fixed' : 'tw-relative',
        className,
      )}
      style={
        isFixed
          ? {
              top: `${
                getElementHeight(getStickyHeaderElement()) - 1
              }px`,
            }
          : {}
      }
    >
      {isLeftButtonVisible && (
        <div
          className="tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-items-center tw-cursor-pointer tw-w-12 tw-justify-center tw-bg-white/80 tw-flex tw-shadow-sm"
          onClick={() => handleArrowClicked(-150)}
        >
          <i className="fas fa-chevron-left" />
        </div>
      )}
      {isRightButtonVisible && (
        <div
          className="tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-items-center tw-cursor-pointer tw-w-12 tw-justify-center tw-bg-white/80 tw-flex tw-shadow-sm"
          onClick={() => handleArrowClicked(150)}
        >
          <i className="fas fa-chevron-right"></i>
        </div>
      )}
      <div
        className="tw-flex tw-w-full no-scrollbar tw-overflow-auto tw-shadow-md tw-px-4"
        ref={tabListRef}
      >
        <ul
          className={cn(
            'tw-flex tw-pl-0 tw-mb-0 tw-list-none tw-flex-nowrap',
          )}
          role="tablist"
        >
          {categories.map((category) => {
            // @ts-ignore
            const to = pathToTo(routePaths.PRODUCTS, {
              // @ts-ignore
              queryParams: { category_ids: category.id.split(',') },
            });

            return (
              <li
                key={category.id}
                className="tw-flex tw-m-0 tw-whitespace-nowrap"
              >
                <Link
                  to={to}
                  exact="query"
                  shallow
                  style={{ color: 'unset' }}
                  className="tw-font-bold tw-p-4 tw-leading-tight"
                  activeClassName="tw-text-primary"
                >
                  <>{category.name}</>
                </Link>
              </li>
            );
          })}
          {collections.length &&
            collections.map((collection) => {
              // @ts-ignore
              const to = pathToTo(routePaths.PRODUCTS, {
                // @ts-ignore
                queryParams: { collection_ids: collection.id },
              });

              return (
                <li
                  key={collection.id}
                  className="tw-flex tw-m-0 tw-whitespace-nowrap"
                >
                  <Link
                    to={to}
                    exact="query"
                    shallow
                    style={{ color: 'unset' }}
                    className="tw-font-bold tw-p-4 tw-leading-tight"
                    activeClassName="tw-text-primary"
                  >
                    <>{collection.name}</>
                  </Link>
                </li>
              );
            })}
          {specialProductCategories.length > 0 &&
            specialProductCategories.map((category) => {
              // @ts-ignore
              const to = pathToTo(routePaths.PRODUCTS, {
                // @ts-ignore
                queryParams: { category_ids: category.id.split(',') },
              });

              return (
                <li
                  key={category.id}
                  className="tw-flex tw-m-0 tw-whitespace-nowrap"
                >
                  <Link
                    to={to}
                    exact="query"
                    shallow
                    style={{ color: 'unset' }}
                    className="tw-font-bold tw-p-4 tw-leading-none"
                    activeClassName="tw-text-primary"
                  >
                    <span className="tw-flex tw-items-center tw-gap-2">
                      {isLoading ? null : isTieredUser ? (
                        <i className="la la-star tw-text-[14px] tw-shrink-0" />
                      ) : (
                        <i className="fa fa-lock tw-text-[14px] tw-shrink-0" />
                      )}
                      {category.name}
                    </span>
                  </Link>
                </li>
              );
            })}
        </ul>
      </div>
    </div>
  );
};

export default ProductCategoriesTabs;
